/**
 * Sicherung erstellen und herunterladen.
 */
import { db, DATA_TABLE_NAMES } from '@/db/schema';
import { setMeta, META_LAST_EXPORT } from '@/db/repositories/meta';
import { CURRENT_SCHEMA_VERSION } from '@/domain/types';
import { nowInstant } from '@/lib/datetime';
import { APP_MARKER, countRows } from '@/lib/backup/format';
import type { BackupFile } from '@/lib/backup/schema';
import { encodeAttachmentRows } from '@/lib/backup/attachments';

const APP_VERSION = '0.1.0';

/** Liest alle Nutzdaten in einer konsistenten Transaktion aus. */
export async function buildBackup(): Promise<BackupFile> {
  const data: Record<string, Record<string, unknown>[]> = {};

  await db.transaction('r', db.dataTables, async () => {
    for (const name of DATA_TABLE_NAMES) {
      data[name] = (await db.table(name).toArray()) as Record<string, unknown>[];
    }
  });

  // Anhaenge nach Base64, sonst macht JSON.stringify aus den Bytes ein leeres
  // Objekt. Die Wandlung ist synchron, weil `bytes` bereits rohe Bytes sind -
  // mit einem Blob braeuchte es ein `await`, das die Lese-Transaktion oben
  // unter den Fuessen wegziehen wuerde.
  data.attachments = encodeAttachmentRows(data.attachments ?? []);

  return {
    app: APP_MARKER,
    schemaVersion: CURRENT_SCHEMA_VERSION,
    appVersion: APP_VERSION,
    exportedAt: nowInstant(),
    counts: countRows(data),
    data,
  };
}

/** Die Sicherung als Text - dasselbe Format fuer Download und Ordner (F-46). */
export function backupJson(backup: BackupFile): string {
  return JSON.stringify(backup, null, 2);
}

/** Merkt sich eine bewusste Sicherung; daran haengt die Erinnerung. */
export async function markExported(at: string): Promise<void> {
  await setMeta(META_LAST_EXPORT, at);
}

export function backupFilename(when = new Date()): string {
  const iso = when.toISOString().slice(0, 10);
  return `ole-privat-sicherung-${iso}.json`;
}

/**
 * Loest den Browser-Download aus.
 *
 * Die Object-URL wird erst im naechsten Makrotask freigegeben: Safari bricht
 * den Download ab, wenn sie noch im selben Tick widerrufen wird.
 */
export function downloadJson(content: string, filename: string): void {
  const blob = new Blob([content], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 0);
}

/**
 * Erstellt eine Sicherung und laedt sie herunter.
 *
 * `markAsExport` steuert, ob der Zeitstempel der letzten Sicherung
 * fortgeschrieben wird: die automatische Sicherheitskopie vor einem Import
 * soll den Export-Erinnerer NICHT zuruecksetzen, denn sie war keine bewusste
 * Sicherung durch den Nutzer.
 */
export async function exportBackup(markAsExport = true): Promise<BackupFile> {
  const backup = await buildBackup();
  downloadJson(backupJson(backup), backupFilename());
  if (markAsExport) {
    await markExported(backup.exportedAt);
  }
  return backup;
}
